import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase, safeChannel } from "./supabase";
import { findRecipe } from "../data/recipes";
import { BoodaTimer } from "./boodaTimer";
import { inferTimersFromInstruction } from "./inferTimersFromInstruction";
import { inferStepKind } from "./inferStepKind";
import { isCookSessionClaimed } from "./cookSessionClaim";
import { paletteEntry } from "./participantPalette";
import { absoluteAvatarURL } from "./absoluteAvatarURL";

// useTeamLiveActivity — mirror the lead's iOS Live Activity onto every
// participant's device.
//
// The user-experience goal: when Dad taps START on Boerenkool, every
// person tagged on the meal — Mom (helper), the kids (diners) —
// sees a lock-screen card that reads "Trevor is cooking Boerenkool ·
// step 3 of 6 · ~28 min to eat time." Nobody has to open the app.
//
// Architecture:
//   * Lead's device runs CookMode and drives BoodaTimer locally
//     (existing path — this hook is silent for the lead).
//   * Every other participant's device:
//       1. Queries cook_session_participants → finds active sessions
//          they belong to (helper/diner roles).
//       2. Resolves the recipe via the same findRecipe cascade
//          useActiveCookSession uses, so timers + title are local.
//       3. Subscribes to realtime on cook_sessions + cook_session_steps
//          for those session ids (RLS already family-readable).
//       4. Calls BoodaTimer.startSession with clientId = `cook-<id>`
//          (the SAME id the lead's device uses — but each device has
//          its own local Live Activity; they share state by both
//          listening to the same Supabase rows).
//       5. On every realtime update, calls BoodaTimer.updateSession.
//       6. On status → finished/abandoned, calls BoodaTimer.endSession.
//
// No-op on web; no-op for the lead (they already have the real
// CookMode-driven Live Activity).

const IS_IOS_NATIVE =
  typeof window !== "undefined"
  && Capacitor.isNativePlatform?.()
  && Capacitor.getPlatform?.() === "ios";

// Module-level guard for the orphan-sweep path inside the hook below.
// The sweep clears Live Activities the user shouldn't be seeing on
// THIS launch. It must run exactly once — re-running on every effect
// re-mount races freshly-spawned activities into the trash. See the
// comment at the call-site for the full rationale.
let orphanSweepDone = false;

// Mirror the timer-extraction logic from CookMode so the participant
// Live Activity carries the same per-step timer list. Kept here as a
// local helper so we don't import a CookMode-side function (avoiding
// cycle between an App-mounted hook and a leaf component).
function buildTimersFromRecipe(recipe) {
  const arr = recipe?.steps || [];
  const timers = [];
  arr.forEach((s, stepIdx) => {
    const stepTitle = s.title || `Step ${stepIdx + 1}`;
    const hasRaw = Array.isArray(s.timers) && s.timers.length > 0;
    const inferred = (hasRaw || s.timer)
      ? null
      : inferTimersFromInstruction(s.instruction || "");
    const list = hasRaw
      ? s.timers
      : (inferred?.timers || (s.timer || inferred?.timer
          ? [{ label: "", seconds: s.timer || inferred?.timer }]
          : []));
    const stepKind = inferStepKind(stepTitle, s.instruction);
    list.forEach((t, tIdx) => {
      const total = Math.max(0, Math.trunc(Number(t?.seconds) || 0));
      if (total <= 0) return;
      timers.push({
        id: `${s.id ?? stepIdx}-${tIdx}`,
        stepIndex: stepIdx,
        stepTitle,
        label: String(t?.label ?? ""),
        totalSeconds: total,
        stepKind,
      });
    });
  });
  return timers;
}

export function useTeamLiveActivity(userId, { findUserRecipe } = {}) {
  // Tracking: which client ids we've started, so we can update +
  // end them deterministically. Stable across re-renders.
  const activeRef = useRef(new Map()); // sessionId -> { clientId, leadId }
  const channelRef = useRef(null);
  // findUserRecipe identity flips every time useUserRecipes' `recipes`
  // state mutates (save/load/realtime). Without ref-routing, the effect
  // below would re-mount on every recipe change → the orphan sweep
  // would re-fire and nuke a CookMode-spawned activity that hadn't
  // propagated to cook_sessions yet. Pin to a ref; effect deps drop
  // to [userId] only.
  const findUserRecipeRef = useRef(findUserRecipe);
  useEffect(() => {
    findUserRecipeRef.current = findUserRecipe;
  }, [findUserRecipe]);

  useEffect(() => {
    if (!IS_IOS_NATIVE || !userId) return undefined;

    let alive = true;
    const active = activeRef.current;

    // Fetch session row + active step + queued timer for one session,
    // then push the right BoodaTimer call. Used both on initial load
    // and on every realtime nudge.
    async function syncSession(sessionId) {
      try {
        // Fetch ALL open step rows (not just the most recent) so we
        // can build the helpers[] array for multi-row lock-screen
        // rendering. The PRIMARY timer slot still goes to the most-
        // recently-started row; everything else lands in helpers[].
        const [{ data: session }, { data: stepRows }] = await Promise.all([
          supabase
            .from("cook_sessions")
            .select("id, user_id, recipe_slug, recipe_title, status")
            .eq("id", sessionId)
            .maybeSingle(),
          supabase
            .from("cook_session_steps")
            .select("step_id, step_title, user_id, started_at, finished_at, nominal_seconds, timer_state")
            .eq("cook_session_id", sessionId)
            .is("finished_at", null)
            .order("started_at", { ascending: false }),
        ]);
        if (!alive) return;
        if (!session) return;

        // If THIS device has CookMode active for this session, that
        // component owns the Live Activity — defer to it. Note we do
        // NOT skip just because we're the lead: if the lead started
        // cooking from desktop, their phone (running this hook with
        // no CookMode claim) should still mirror the lock-screen card.
        if (isCookSessionClaimed(session.id)) return;

        const cid = `cook-${session.id}`;

        // End if the lead finished or abandoned. We fire endSession
        // UNCONDITIONALLY (not just when `active.has(session.id)`)
        // because the activity may have been spawned by CookMode on
        // this same device — that path doesn't put a row in our
        // local `active` Map. Without this, completed cooks left
        // their Live Activity card on the lock screen until the
        // staleDate (11h) elapsed. Result: lock-screen card stack.
        if (session.status !== "active") {
          await BoodaTimer.endSession({ clientId: cid, immediate: true })
            .catch(() => { /* ignore — activity may not exist locally */ });
          active.delete(session.id);
          return;
        }

        // Resolve the recipe for timers + title. When the session
        // belongs to someone else (team cook from a participant
        // device), fetch the LEAD's user_recipe so the lock-screen
        // card matches their actual recipe (not a bundled
        // lookalike). Otherwise the caller's resolver / bundled
        // cascade.
        let recipe = null;
        if (session.user_id && session.user_id !== userId) {
          try {
            const { data: ur } = await supabase
              .from("user_recipes")
              .select("recipe, slug")
              .eq("slug", session.recipe_slug)
              .eq("user_id", session.user_id)
              .maybeSingle();
            if (ur?.recipe) recipe = { ...ur.recipe, slug: ur.slug };
          } catch {}
        }
        if (!recipe) {
          recipe = findRecipe(session.recipe_slug, findUserRecipeRef.current) || null;
        }
        const timers = recipe ? buildTimersFromRecipe(recipe) : [];

        // Find what step the lead is currently on, and which flat
        // timer index that maps to.
        //
        // step_id from cook_session_steps takes one of two shapes:
        //   • "<stepId>"         — single-Timer step (Sear Steak ·
        //                           one timer of 8 min)
        //   • "<stepId>-s<N>"    — TimerStack stage (Sear Steak with
        //                           stages reduce 2m / cover 3m / rest
        //                           3m → step_id="3-s0", "3-s1", "3-s2")
        // We need BOTH halves to pick the right entry in the flat
        // `timers` array — the previous code stripped the suffix and
        // always returned the first timer of the step, which made the
        // mirror Live Activity sit on stage 0 even when the lead had
        // advanced to stage 2. That's the "never on the right step"
        // symptom.
        const liveStepRow = stepRows?.[0] || null;
        let currentStepIndex = -1;
        let activeTimerIndex = -1;
        let endsAt = null;
        let stageIdx = 0;
        let baseStepId = null;
        if (liveStepRow) {
          const rawId = String(liveStepRow.step_id || "");
          const stageMatch = rawId.match(/-s(\d+)$/);
          stageIdx = stageMatch ? Number(stageMatch[1]) : 0;
          baseStepId = stageMatch ? rawId.slice(0, stageMatch.index) : rawId;
        }
        if (liveStepRow && recipe?.steps && baseStepId) {
          const idx = recipe.steps.findIndex((s) => String(s.id) === baseStepId);
          // Don't fall back to step 0 on a miss — that silently shows
          // the wrong step on the lock screen. Skip the update; the
          // next realtime tick will re-try, and a recipe-side renaming
          // mismatch surfaces in the console rather than as silently
          // wrong UI.
          if (idx < 0) {
            console.warn(
              "[teamLiveActivity][sync] step_id not found in recipe.steps — skipping update",
              { sessionId: session.id, rawStepId: liveStepRow.step_id, baseStepId,
                knownIds: recipe.steps.slice(0, 8).map((s) => s.id) },
            );
            return;
          }
          currentStepIndex = idx;

          // Pick the timer at (stepIndex, stageIdx). TimerStack stages
          // are emitted in order by buildTimersFromRecipe so the Nth
          // timer of stepIndex matches the lead's stage. Falls back to
          // the first timer of the step if the stage is out of range
          // (defensive — recipe re-ordering edge case).
          const timersForStep = timers
            .map((t, flatIdx) => ({ flatIdx, ...t }))
            .filter((t) => t.stepIndex === currentStepIndex);
          const pick = timersForStep[stageIdx] || timersForStep[0] || null;
          activeTimerIndex = pick ? pick.flatIdx : -1;

          // Prefer the server-truth timer_state (migration 0214) — it
          // reflects the lead's actual pause/reset cadence. Falls back
          // to the legacy started_at + nominal_seconds for rows
          // written before the column landed. When timer_state exists
          // but no endsAt (READY state), endsAt stays null so the
          // Live Activity shows the pre-start UI instead of a stale
          // countdown.
          const ts = liveStepRow.timer_state || null;
          if (ts?.endsAt) {
            endsAt = ts.endsAt;
          } else if (!ts && liveStepRow.started_at && liveStepRow.nominal_seconds) {
            const startedMs = new Date(liveStepRow.started_at).getTime();
            endsAt = new Date(startedMs + liveStepRow.nominal_seconds * 1000).toISOString();
          }
          // Don't render an ENDED timer as "active" — clears the
          // active-timer index so the lock-screen rail stops pulsing.
          if (ts?.ended) activeTimerIndex = -1;
        }
        // currentStepIndex stays -1 when there's no liveStepRow (no
        // step active yet) OR the recipe has no steps; downstream
        // BoodaTimer expects a non-negative integer, default to 0 for
        // the "pre-first-step" overview state.
        const safeStepIndex = currentStepIndex >= 0 ? currentStepIndex : 0;
        const stepTitle = liveStepRow?.step_title
          || recipe?.steps?.[safeStepIndex]?.title
          || `Step ${safeStepIndex + 1}`;

        // Surface pause/reset to the lock-screen Live Activity. iOS
        // already knows how to render `isPaused` + `pausedRemaining`
        // from BoodaTimerAttributes.ContentState — we just have to
        // hand it the values from the server-truth timer_state.
        const tsForState = liveStepRow?.timer_state || null;
        const isPaused = !!tsForState?.isPaused;
        const pausedRemaining = tsForState?.isPaused
          ? Number(tsForState.pausedRemainingSec) || 0
          : null;

        // activeTimerStartedAt = endsAt - nominal_seconds. This is
        // the lead's actual tap-Start time, NOT step entry time. The
        // widget's elapsed math uses (now - activeTimerStartedAt)
        // capped against totalSeconds — so feeding it step-entry-time
        // makes "elapsed" much larger than reality and the lock-screen
        // bar drains too fast.
        let activeTimerStartedAt = null;
        if (endsAt && liveStepRow?.nominal_seconds) {
          const endsMs = new Date(endsAt).getTime();
          activeTimerStartedAt = new Date(endsMs - Number(liveStepRow.nominal_seconds) * 1000).toISOString();
        } else if (liveStepRow?.started_at) {
          activeTimerStartedAt = liveStepRow.started_at;
        }

        // CRITICAL: Swift plugin's parseStateDict reads dict keys
        // `activeTimerEndsAt` / `activeTimerStartedAt`, NOT `endsAt`
        // / `startedAt`. Before this rename the plugin silently
        // dropped the values → activeTimerEndsAt arrived nil on the
        // widget side → Text(timerInterval:) rendered 0:00 → total
        // math excluded the active step's remaining time.
        //
        // completedTimerIndices is intentionally NOT pushed from
        // here. The cook_session_steps.finished_at signal is too
        // noisy — startStep auto-finishes prior rows as the lead
        // navigates between steps, so a row's finished_at != null
        // doesn't reliably mean "user has actually completed this
        // step." Letting Swift default to [] keeps the widget's
        // total math correct (full upcoming + active remaining)
        // until we have a cleaner "definitively done" signal.
        // Fetch participants + assignments — needed for owner colors on
        // the rail (every timer segment) AND for the helpers[] compact
        // strip below it. Both queries scoped to this session id.
        const [{ data: participants }, { data: assignments }] = await Promise.all([
          supabase
            .from("cook_session_participants")
            .select("user_id, color_tag")
            .eq("cook_session_id", session.id)
            .is("left_at", null),
          supabase
            .from("cook_step_assignments")
            .select("step_id, assigned_to, status")
            .eq("cook_session_id", session.id)
            .in("status", ["pending", "accepted", "in_progress"]),
        ]);
        const participantsById = new Map();
        for (const p of (participants || [])) participantsById.set(p.user_id, p);
        const assignmentsByStep = new Map();
        for (const a of (assignments || [])) {
          const key = String(a.step_id);
          const list = assignmentsByStep.get(key) || [];
          list.push(a);
          assignmentsByStep.set(key, list);
        }

        // Build helpers[] — every OTHER open step row (besides the
        // primary liveStepRow), with profile name hydrated. The
        // widget renders one compact pill per helper below the
        // primary timer. Empty array = solo cook → widget collapses
        // the helper strip entirely.
        const otherRows = (stepRows || []).filter((r) => r !== liveStepRow);
        // Union of every user_id we'll need a profile for:
        //   - other open step rows (helpers[] strip)
        //   - assignment assignees (rail owner colors + names)
        //   - the session lead (default owner of unassigned steps)
        const profileIds = new Set();
        for (const r of otherRows) if (r.user_id) profileIds.add(r.user_id);
        for (const a of (assignments || [])) {
          if (a.assigned_to) profileIds.add(a.assigned_to);
        }
        if (session.user_id) profileIds.add(session.user_id);
        const profilesById = new Map();
        if (profileIds.size > 0) {
          const { data: profs } = await supabase
            .from("profiles")
            .select("id, name, avatar_url")
            .in("id", Array.from(profileIds));
          for (const p of (profs || [])) profilesById.set(p.id, p);
        }

        const firstName = (uid) => {
          const p = profilesById.get(uid);
          const full = p?.name || "";
          return full ? full.split(/\s+/)[0] : "Cook";
        };

        // Dedupe by COMPOSITE KEY (userId + stepIndex) so:
        //   • TimerStack stages of the same step collapse to one entry
        //   • Parallel timers across different steps produce SEPARATE
        //     entries (e.g. user with step 1 and step 3 both ticking)
        const helpersByKey = new Map();
        // Pre-compute a stepLabelMap from the flat timers list so we
        // can resolve each row → its EXACT flat-timer segment. The
        // map shape mirrors what CookMode builds inline: stepIdx →
        // [{label, flatIdx}, ...] in stage order.
        const stepLabelMap = {};
        timers.forEach((t, flatIdx) => {
          if (!stepLabelMap[t.stepIndex]) stepLabelMap[t.stepIndex] = [];
          stepLabelMap[t.stepIndex].push({ label: t.label || "", flatIdx });
        });
        for (const r of otherRows) {
          const rTs = r.timer_state || null;
          const rEndsAt = rTs?.endsAt || null;
          const rIsPaused = !!rTs?.isPaused;
          const rPausedRem = rTs?.isPaused
            ? Number(rTs.pausedRemainingSec) || 0
            : null;
          // activeTimerStartedAt: derived from (endsAt - nominal_seconds)
          // when available so the widget can render a ProgressView range
          // for this helper's active timer. Falls back to r.started_at
          // for legacy rows without timer_state.
          let rStartedAt = null;
          if (rEndsAt && r.nominal_seconds) {
            const endsMs = new Date(rEndsAt).getTime();
            rStartedAt = new Date(endsMs - Number(r.nominal_seconds) * 1000).toISOString();
          } else if (r.started_at) {
            rStartedAt = r.started_at;
          }
          const rRawId = String(r.step_id || "");
          const rStageMatch = rRawId.match(/-s(\d+)$/);
          const rStageIdx = rStageMatch ? Number(rStageMatch[1]) : 0;
          const rBaseId = rStageMatch ? rRawId.slice(0, rStageMatch.index) : rRawId;
          const rIdx = recipe?.steps
            ? recipe.steps.findIndex((s) => String(s.id) === rBaseId)
            : -1;
          const safeIdx = rIdx >= 0 ? rIdx : 0;
          const rTitle = r.step_title
            || recipe?.steps?.[safeIdx]?.title
            || `Step ${safeIdx + 1}`;
          // Resolve flat-timer segment using the precomputed map.
          // Multi-stage steps: pick stagesForStep[stageIdx].flatIdx.
          // Single-Timer steps: only one entry, always use index 0.
          const stagesForStep = stepLabelMap[safeIdx];
          const flatTimerIndex = (Array.isArray(stagesForStep) && stagesForStep.length > 0)
            ? (stagesForStep.length === 1
                ? stagesForStep[0].flatIdx
                : (stagesForStep[rStageIdx]?.flatIdx ?? stagesForStep[0].flatIdx))
            : -1;
          const entry = {
            userId:               r.user_id || "",
            name:                 r.user_id ? firstName(r.user_id) : "Helper",
            currentStepIndex:     safeIdx,
            currentStepTitle:     rTitle,
            activeTimerEndsAt:    rEndsAt,
            activeTimerStartedAt: rStartedAt,
            isPaused:             rIsPaused,
            pausedRemaining:      rPausedRem,
            flatTimerIndex,
          };
          if (!entry.userId) continue;
          // Composite-key dedupe by (userId, flatTimerIndex) so
          // multi-stage parallel work within ONE step produces N
          // distinct entries (one per stage). Falls back to
          // (userId, stepIdx) when flatTimerIndex is -1 (recipe-
          // shape mismatch) so a row never silently disappears.
          const nowMs = Date.now();
          const score = (() => {
            if (rTs?.endsAt) {
              const endsMs = new Date(rTs.endsAt).getTime();
              if (Number.isFinite(endsMs) && endsMs > nowMs) {
                return rTs.isPaused ? 2 : 3;
              }
            }
            return rTs ? 1 : 0;
          })();
          const startedMs = r.started_at ? new Date(r.started_at).getTime() : 0;
          const key = flatTimerIndex >= 0
            ? `${entry.userId}:f${flatTimerIndex}`
            : `${entry.userId}:s${safeIdx}`;
          const prev = helpersByKey.get(key);
          const winsBetter = !prev
            || score > prev.score
            || (score === prev.score && startedMs > prev.startedMs);
          if (winsBetter) {
            helpersByKey.set(key, { score, startedMs, entry });
          }
        }
        const helpers = Array.from(helpersByKey.values()).map((v) => v.entry);

        // Build timerOwners[] — parallel to timers[], one entry per
        // flat-timer index. Owner resolves from assignment OR falls
        // back to the lead. Color comes from cook_session_participants
        // .color_tag via paletteEntry.
        const timerOwners = timers.map((t) => {
          const step = recipe?.steps?.[t.stepIndex];
          const stepId = step?.id != null ? String(step.id) : null;
          let ownerId = null;
          if (stepId) {
            const list = assignmentsByStep.get(stepId) || [];
            const active = list.find((a) =>
              a.status === "accepted" ||
              a.status === "in_progress" ||
              a.status === "pending"
            );
            if (active?.assigned_to) ownerId = active.assigned_to;
          }
          if (!ownerId) ownerId = session.user_id || null;
          if (!ownerId) return null;
          const part = participantsById.get(ownerId);
          const colorHex = paletteEntry(part?.color_tag || "slate").ring;
          const prof = profilesById.get(ownerId);
          return {
            userId: ownerId,
            name: firstName(ownerId),
            colorHex,
            avatarURL: absoluteAvatarURL(prof?.avatar_url),
          };
        }).filter(Boolean);

        const initial = {
          activeTimerIndex,
          activeTimerEndsAt:    endsAt,
          activeTimerStartedAt: activeTimerStartedAt,
          currentStepIndex:     safeStepIndex,
          currentStepTitle:     stepTitle,
          isPaused,
          pausedRemaining,
          helpers,
          timerOwners,
          // Heartbeat — forces a widget body re-render on every sync
          // even when no other field changed. Pairs with the local
          // Activity.update path in BoodaTimer's plugin, which parses
          // this as ISO. Server cron tick path uses Unix-seconds-Double.
          lastTickAt:           new Date().toISOString(),
          // Push-driven pulse counter — flips 0 ↔ 1 each sync. Widget
          // binds an animated halo to this value (only push-driven
          // animation works in Live Activities).
          pulseFrame:           Math.floor(Date.now() / 1000) % 2,
          // Recipe hero image — through state so already-running
          // activities update when the recipe's image changes.
          goalImageURL:         recipe?.goalImageURL || recipe?.heroImageURL || null,
        };
        console.info("[teamLiveActivity][sync] pushing state", {
          sessionId: session.id,
          stepId: liveStepRow?.step_id,
          baseStepId,
          stageIdx,
          currentStepIndex: safeStepIndex,
          activeTimerIndex,
          endsAt,
          isPaused,
          timerPhase: tsForState?.ended ? "ended"
                    : tsForState?.isPaused ? "paused"
                    : tsForState?.endsAt ? "ticking"
                    : tsForState === null ? "legacy"
                    : "ready",
        });

        if (!active.has(session.id)) {
          // First time we've seen this session — spawn the mirror.
          active.set(session.id, { clientId: cid, leadId: session.user_id });
          await BoodaTimer.startSession({
            clientId: cid,
            recipeName: session.recipe_title || recipe?.title || "Cooking",
            goalImageURL: recipe?.goalImageURL || recipe?.heroImageURL || null,
            timers,
            initial,
          }).catch((e) => console.warn("[teamLiveActivity] startSession failed:", e?.message || e));
        } else {
          // Already mirroring — just push the latest state.
          await BoodaTimer.updateSession({
            clientId: cid,
            state: initial,
          }).catch((e) => console.warn("[teamLiveActivity] updateSession failed:", e?.message || e));
        }
      } catch (e) {
        console.warn("[teamLiveActivity] syncSession threw:", e?.message || e);
      }
    }

    // Initial load — find every active session this user can access:
    //   1. Sessions where they're a participant (helper / diner /
    //      sometimes the lead, when scheduling pre-created the row).
    //   2. Sessions they own (lead, started from any device).
    //   3. FAMILY-WIDE active sessions — every cook a family member is
    //      currently running, even when the local user wasn't invited.
    //      RLS on cook_sessions / cook_session_steps already includes
    //      `user_id in family_ids_of(auth.uid())`, so the SELECT just
    //      works. This is what makes "Trevor can glance at Marissa's
    //      broccoli timer from his lock screen even though he's not a
    //      participant in her cook" possible. syncSession's existing
    //      logic spawns one Live Activity per session id, so multiple
    //      family cooks stack up cleanly on the lock screen.
    // Then sync each — syncSession itself defers to a CookMode claim
    // if this device is the one driving the activity locally.
    (async () => {
      try {
        // Fetch ALL active cook_sessions visible to this user. RLS
        // returns: own + family-of-self + (anything participant view
        // covers via the participants table). We then deduplicate
        // against the participant set so syncSession isn't called
        // twice for the same id.
        const [partsRes, allActiveRes] = await Promise.all([
          supabase
            .from("cook_session_participants")
            .select("cook_session_id, cook_sessions!inner(id, user_id, status)")
            .eq("user_id", userId)
            .is("left_at", null),
          supabase
            .from("cook_sessions")
            .select("id, user_id, status")
            .eq("status", "active"),
        ]);
        if (!alive) return;
        if (partsRes.error) console.warn("[teamLiveActivity] participants fetch:", partsRes.error.message);
        if (allActiveRes.error) console.warn("[teamLiveActivity] active-cooks fetch:", allActiveRes.error.message);
        const ids = new Set();
        for (const p of (partsRes.data || [])) {
          if (p.cook_sessions?.status === "active") ids.add(p.cook_session_id);
        }
        for (const s of (allActiveRes.data || [])) ids.add(s.id);
        console.info("[teamLiveActivity] active sessions found:", Array.from(ids));

        // ORPHAN SWEEP — dismiss any Live Activity on this device
        // that doesn't correspond to a currently-active cook the
        // user can see. Catches the stack-up case where previous
        // cooks were abandoned without flipping status, force-quits,
        // or activities spawned by code paths that didn't end them
        // (CookMode crash, in-flight session rollback). Runs BEFORE
        // we spawn fresh mirrors so the lock screen lands in the
        // intended state.
        //
        // ONCE PER PROCESS only. Re-running on every userId change
        // (or anything else that re-mounts this effect) races against
        // CookMode-spawned activities whose cook_sessions row hasn't
        // propagated to participants/owned queries yet — the sweep
        // would see an empty keep-list and nuke the brand-new card.
        // The cleanup paths (status flip → endSession in syncSession,
        // hook unmount → end every entry in `active`) are sufficient
        // to manage state after first launch.
        if (!orphanSweepDone) {
          orphanSweepDone = true;
          const keepClientIds = Array.from(ids).map((id) => `cook-${id}`);
          console.info("[teamLiveActivity] orphan sweep — keeping:", keepClientIds);
          await BoodaTimer.endSessionsExcept({ keepClientIds, immediate: true })
            .catch((e) => console.warn("[teamLiveActivity] orphan sweep failed:", e?.message || e));
        }

        for (const sid of ids) {
          // eslint-disable-next-line no-await-in-loop
          await syncSession(sid);
        }
      } catch (e) {
        console.warn("[teamLiveActivity] initial load threw:", e?.message || e);
      }
    })();

    // Realtime: tail cook_sessions (status / recipe changes) AND
    // cook_session_steps (lead advances steps) for any session id
    // we've seen via cook_session_participants. We don't filter on
    // session_id here — RLS already restricts what the user can read,
    // and a stranger's session would never reach the channel.
    const ch = safeChannel(`rt:team-live-activity:${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_sessions" },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row?.id) return;
          // Only react when the row affects a session we care about,
          // OR a brand-new active session arrives that lists us as a
          // participant. The participants query runs lazily inside
          // syncSession via the helper; here we just re-sync.
          syncSession(row.id);
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps" },
        (payload) => {
          const sid = payload.new?.cook_session_id || payload.old?.cook_session_id;
          if (!sid) return;
          if (active.has(sid)) syncSession(sid);
        },
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "cook_session_participants", filter: `user_id=eq.${userId}` },
        (payload) => {
          const sid = payload.new?.cook_session_id;
          if (sid) syncSession(sid);
        },
      )
      // Lead-from-other-device case: when the user starts cooking on
      // their desktop, their iPhone (running this hook) needs to pick
      // up the new cook_sessions row immediately. Listening here on
      // user_id=eq for the user's own inserts.
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "cook_sessions", filter: `user_id=eq.${userId}` },
        (payload) => {
          const sid = payload.new?.id;
          if (sid) syncSession(sid);
        },
      )
      .subscribe();
    channelRef.current = ch;

    return () => {
      alive = false;
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch { /* ignore */ }
        channelRef.current = null;
      }
      // Tear down every mirror Live Activity we spawned. Lead's own
      // CookMode-driven activity is untouched (different clientId
      // ownership; lead's device doesn't run this hook against its
      // own sessions).
      for (const { clientId } of active.values()) {
        BoodaTimer.endSession({ clientId, immediate: false }).catch(() => { /* ignore */ });
      }
      active.clear();
    };
    // findUserRecipe is intentionally NOT a dep — its identity flips
    // every time useUserRecipes' state mutates. We read the latest via
    // findUserRecipeRef.current inside syncSession.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
}
